import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { finalize, first } from 'rxjs/operators'
import { HubtypeBot } from '../../models/hubtype-bot'
import { HubtypeBotConfig } from '../../models/hubtype-bot-config'
import { HubtypeOrganization } from '../../models/hubtype-organization'
import {
  HubtypeProviderAccount,
  WebchatSettings,
} from '../../models/hubtype-provider-account'
import * as fromRoot from '../../reducers'
import { BotService } from '../../services/hubtype-api/bot.service'
import { ProviderAccountService } from '../../services/hubtype-api/provider-account.service'
import { AngularComponent } from '../AngularComponent'

@Component({
  selector: 'app-channel-detail',
  templateUrl: './channel-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./channel-detail.component.scss'],
  standalone: false,
})
export class ChannelDetailComponent
  extends AngularComponent
  implements OnInit, OnDestroy
{
  providerAccount: HubtypeProviderAccount
  webchatSettings: WebchatSettings = { visible: true }
  organization: HubtypeOrganization
  providerId: string
  showCodeSnippet = false
  feedback = ''
  botConfig: HubtypeBotConfig | null = null
  updateSettingsLabel: string
  private bot: HubtypeBot | null = null

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private providerAccountService: ProviderAccountService,
    private botService: BotService,
    private translate: TranslateService
  ) {
    super()
    this.updateSettingsLabel = this.translate.instant(
      'shared.channelDetail.updateSettings'
    )
    this.subscribeUntilDestroy(this.route.paramMap, (params: ParamMap) => {
      this.webchatSettings = undefined
      this.providerId = params.get('provider_id')
      this.ngOnInit()
    })
    this.subscribeUntilDestroy(this.store.select(fromRoot.getBot), b => {
      this.ref.markForCheck()
      this.bot = b
      if (b?.id) {
        this.loadBotConfig(b.id)
      }
    })
  }

  get title(): string {
    return this.translate.instant('shared.channelDetail.title', {
      name: this.providerAccount?.name,
    })
  }

  ngOnInit() {
    this.providerAccountService
      .getProvider(this.providerId)
      .pipe(
        first(),
        finalize(() => this.ref.markForCheck())
      )
      .subscribe(
        res => {
          this.providerAccount = res
          let settings = res.credentials_json.settings
          if (settings) {
            this.webchatSettings = {
              visible: settings.visible,
              whitelisted_urls: settings.whitelisted_urls,
              scheduled_queue_id: settings.scheduled_queue_id,
            }
          }
        },
        err =>
          console.log(`Provider with id ${this.providerId} does not exist`, err)
      )
  }

  private loadBotConfig(botId: string) {
    this.subscribeUntilDestroy(
      this.botService
        .getBotConfig(botId)
        .pipe(finalize(() => this.ref.markForCheck())),
      config => {
        this.botConfig = config
      },
      () => {
        // If bot config fails to load, continue without it
        this.botConfig = null
      }
    )
  }

  onWebchatSettingsChanged(updatedSettings: WebchatSettings): void {
    this.webchatSettings = { ...this.webchatSettings, ...updatedSettings }
  }

  showFeedback() {
    if (this.feedback) {
      setTimeout(() => {
        this.ref.markForCheck()
        this.feedback = null
      }, 5000)
      return true
    }
    return false
  }

  updateSettings(): void {
    const previousSettings = this.providerAccount.credentials_json.settings
    this.providerAccount.credentials_json.settings = {
      ...previousSettings,
      ...this.webchatSettings,
    }
    this.providerAccountService
      .updateWebchatSettings(this.providerId, this.webchatSettings)
      .pipe(
        first(),
        finalize(() => this.ref.markForCheck())
      )
      .subscribe(
        res => {
          this.feedback = this.translate.instant(
            'shared.channelDetail.updatedSuccessfully'
          )
        },
        err => {
          const errorMsg = this.translate.instant(
            'shared.channelDetail.externalErrorUpdatingSettings'
          )
          this.feedback = errorMsg
          console.log(errorMsg, err)
        }
      )
  }

  back() {
    this.backRoute(this.router, this.route)
  }
}
